import { DestroyRef, inject, Injectable, Signal, signal } from '@angular/core';
import { ThemePreferenceService, ThemeMode } from './theme-preference.service';

/**
 * Store for managing application theme state (light/dark/system mode).
 * Applies initial theme on construction and persists user preference.
 *
 * Note: To prevent flash of wrong theme, add this script to index.html before Angular loads:
 * ```html
 * <script>
 *   // Apply theme early to prevent flash of wrong theme
 *   // Priority: 1. localStorage theme-mode, 2. light mode (default)
 *   (function () {
 *     var isDark = false;
 *     try {
 *       var stored = window.localStorage ? window.localStorage.getItem('theme-mode') : null;
 *       if (stored === 'dark') {
 *         isDark = true;
 *       } else if (stored === 'system') {
 *         isDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
 *       }
 *       // 'light' or null = light mode (default)
 *     } catch (e) {
 *       // Fallback to light mode on error
 *     }
 *     if (isDark) {
 *       document.documentElement.classList.add('dark-mode');
 *     }
 *   })();
 * </script>
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeStore {
  private readonly preference = inject(ThemePreferenceService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _themeMode = signal<ThemeMode>(this.getInitialThemeMode());
  private readonly _isDarkMode = signal(this.computeIsDarkMode(this._themeMode()));

  /** Current theme mode setting (light/dark/system) */
  readonly themeMode: Signal<ThemeMode> = this._themeMode.asReadonly();

  /** Whether dark mode is currently active (resolved from mode + system preference) */
  readonly isDarkMode: Signal<boolean> = this._isDarkMode.asReadonly();

  constructor() {
    // Ensure DOM class is in sync with initial state
    this.preference.applyThemeClass(this._isDarkMode());

    // Listen for system preference changes when in 'system' mode
    const systemCleanup = this.preference.onSystemPreferenceChange((isDark: boolean) => {
      if (this._themeMode() === 'system') {
        this._isDarkMode.set(isDark);
        this.preference.applyThemeClass(isDark);
      }
    });

    // Listen for storage changes from other app instances (tabs/windows)
    const storageCleanup = this.preference.onStorageChange((mode: ThemeMode) => {
      this._themeMode.set(mode);
      this.applyCurrentTheme();
    });

    this.destroyRef.onDestroy(() => {
      systemCleanup();
      storageCleanup();
    });
  }

  private getInitialThemeMode(): ThemeMode {
    return this.preference.getStoredPreference() ?? 'light';
  }

  private computeIsDarkMode(mode: ThemeMode): boolean {
    if (mode === 'system') {
      return this.preference.getSystemPreference();
    }
    return mode === 'dark';
  }

  private applyCurrentTheme(): void {
    const isDark = this.computeIsDarkMode(this._themeMode());
    this._isDarkMode.set(isDark);
    this.preference.applyThemeClass(isDark);
  }

  /** Set theme mode and persist to localStorage */
  setThemeMode(mode: ThemeMode): void {
    const applyTheme = (): void => {
      this._themeMode.set(mode);
      this.preference.savePreference(mode);
      this.applyCurrentTheme();
    };

    // Use View Transitions API for smooth theme switching if supported
    if (document.startViewTransition) {
      document.startViewTransition(applyTheme);
    } else {
      applyTheme();
    }
  }

  /** @deprecated Use setThemeMode instead. Toggles between light and dark only. */
  toggleDarkMode(): void {
    this.setThemeMode(this._isDarkMode() ? 'light' : 'dark');
  }

  /** @deprecated Use setThemeMode instead. Sets dark mode explicitly. */
  setDarkMode(isDark: boolean): void {
    this.setThemeMode(isDark ? 'dark' : 'light');
  }
}
